import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./StudentClassForm.scss";
import {
    Table,
    Button,
    DatePicker,
} from "antd";
import { getApi } from "../../Utlis/apiService";
import {
    GET_STUDENT_ONLINE_CURRENT_DETAILS,
    GET_STUDENT_ONLINE_PAST_DETAILS,
    GET_STUDENT_OFFLINE_CURRENT_DETAILS,
    GET_STUDENT_OFFLINE_PAST_DETAILS
}
    from "../../Utlis/apiPath";

const { RangePicker } = DatePicker;

const Bookings = () => {
    const { id: studentId } = useParams();
    const [activeTab, setActiveTab] = useState("1");
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 0,
        pageSize: 10,
        total: 0,
    });

    const [dateRange, setDateRange] = useState([]);
    const [isOnline, setIsOnline] = useState(true);

    const [offlineTableData, setOfflineTableData] = useState([]);

    // Fetch online bookings and counts
    useEffect(() => {
        if (isOnline) {
            fetchAllBookingsCurrent();
        } else {
            fetchAllBookingsOfflineCurrent();
        }
        // eslint-disable-next-line
    }, [isOnline, pagination.current, pagination.pageSize, dateRange]);

    // Add new functions for online past and current table data
    const fetchAllBookingsPast = async () => {
        setIsLoading(true);
        try {
            const params = {
                studentId,
                ...(dateRange[0] && dateRange[1] && {
                    startDate: dateRange[0].format("YYYY-MM-DD"),
                    endDate: dateRange[1].format("YYYY-MM-DD"),
                }),
            };

            const { status, data } = await getApi(GET_STUDENT_ONLINE_PAST_DETAILS, { params });

            if (status === 200) {
                const formattedData = data.map((booking, index) => ({
                    key: index + 1,
                    className: booking.className,
                    instructor: booking.instructor,
                    startDate: new Date(booking.startDate).toLocaleDateString(),
                    endDate: booking.endDate ? new Date(booking.endDate).toLocaleDateString() : "N/A",
                    amountSpent: `₹${booking.amountSpent}`,
                    status: booking.status,
                }));

                setTableData(formattedData);
                setPagination({
                    ...pagination,
                    total: data.length,
                });
            } else {
                setTableData([]);
            }
        } catch (error) {
            console.error("Error fetching past bookings:", error);
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllBookingsCurrent = async () => {
        setIsLoading(true);
        try {
            const params = {
                studentId,
                ...(dateRange[0] && dateRange[1] && {
                    startDate: dateRange[0].format("YYYY-MM-DD"),
                    endDate: dateRange[1].format("YYYY-MM-DD"),
                }),
            };

            const { status, data } = await getApi(GET_STUDENT_ONLINE_CURRENT_DETAILS, { params });

            if (status === 200) {
                const formattedData = data.map((booking, index) => ({
                    key: index + 1,
                    className: booking.className,
                    instructor: booking.instructor,
                    startDate: new Date(booking.startDate).toLocaleDateString(),
                    endDate: booking.endDate ? new Date(booking.endDate).toLocaleDateString() : "N/A",
                    amountSpent: `₹${booking.amountSpent}`,
                    status: booking.status,
                }));

                setTableData(formattedData);
                setPagination({
                    ...pagination,
                    total: data.length,
                });
            } else {
                setTableData([]);
            }
        } catch (error) {
            console.error("Error fetching current bookings:", error);
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Add new functions for offline past and current table data
    const fetchAllBookingsOfflinePast = async () => {
        setIsLoading(true);
        try {
            const params = {
                studentId,
                ...(dateRange[0] && dateRange[1] && {
                    startDate: dateRange[0].format("YYYY-MM-DD"),
                    endDate: dateRange[1].format("YYYY-MM-DD"),
                }),
            };
            const { status, data } = await getApi(GET_STUDENT_OFFLINE_PAST_DETAILS, { params });
            if (status === 200) {
                const formattedData = data.map((booking, index) => ({
                    key: index + 1,
                    instructorName: booking.instructorName,
                    subscriptionType: booking.subscriptionType,
                    startDate: new Date(booking.startDate).toLocaleDateString(),
                    endDate: new Date(booking.endDate).toLocaleDateString(),
                    amountSpent: `₹${booking.amountSpent}`,
                    status: booking.status,
                }));
                setOfflineTableData(formattedData);
                setPagination({
                    ...pagination,
                    total: data.length,
                });
            } else {
                setOfflineTableData([]);
            }
        } catch (error) {
            setOfflineTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllBookingsOfflineCurrent = async () => {
        setIsLoading(true);
        try {
            const params = {
                studentId,
                ...(dateRange[0] && dateRange[1] && {
                    startDate: dateRange[0].format("YYYY-MM-DD"),
                    endDate: dateRange[1].format("YYYY-MM-DD"),
                }),
            };
            const { status, data } = await getApi(GET_STUDENT_OFFLINE_CURRENT_DETAILS, { params });
            if (status === 200) {
                const formattedData = data.map((booking, index) => ({
                    key: index + 1,
                    instructorName: booking.instructorName,
                    subscriptionType: booking.subscriptionType,
                    startDate: new Date(booking.startDate).toLocaleDateString(),
                    endDate: new Date(booking.endDate).toLocaleDateString(),
                    amountSpent: `₹${booking.amountSpent}`,
                    status: booking.status,
                }));
                setOfflineTableData(formattedData);
                setPagination({
                    ...pagination,
                    total: data.length,
                });
            } else {
                setOfflineTableData([]);
            }
        } catch (error) {
            setOfflineTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTableChange = (pagination) => {
        setPagination({
            ...pagination,
            current: pagination.current,
        });
    };

    const handleDateChange = (dates) => {
        setDateRange(dates);
        setPagination({
            ...pagination,
            current: 0,
        });
    };

    const handleClearFilters = () => {
        setDateRange([]);
        setPagination({
            ...pagination,
            current: 0,
        });
    };

    const columns = [
        {
            title: "SL No",
            key: "slNo",
            render: (text, record, index) =>
                pagination.current * pagination.pageSize + (index + 1),
        },
        { title: "Class Name", dataIndex: "className" },
        { title: "Instructor", dataIndex: "instructor" },
        { title: "Start Date", dataIndex: "startDate" },
        { title: "End Date", dataIndex: "endDate" },
        { title: "Amount Spent", dataIndex: "amountSpent" },
        {
            title: "Status",
            dataIndex: "status",
            render: (status) => (
                <span
                    style={{
                        color:
                            status === "completed"
                                ? "#52c41a"
                                : status === "cancelled"
                                    ? "#f5222d"
                                    : "orange",
                    }}
                >
                    {status}
                </span>
            ),
        },
    ];

    const offlineColumns = [
        {
            title: "SL No",
            key: "slNo",
            render: (text, record, index) =>
                pagination.current * pagination.pageSize + (index + 1),
        },
        { title: "Instructor Name", dataIndex: "instructorName" },
        { title: "Subscription Type", dataIndex: "subscriptionType" },
        { title: "Start Date", dataIndex: "startDate" },
        { title: "End Date", dataIndex: "endDate" },
        { title: "Amount Spent", dataIndex: "amountSpent" },
        {
            title: "Status",
            dataIndex: "status",
            render: (status) => (
                <span
                    style={{
                        color:
                            status === "completed"
                                ? "#52c41a"
                                : status === "cancelled"
                                    ? "#f5222d"
                                    : "#1890ff",
                    }}
                >
                    {status}
                </span>
            ),
        },
    ];

    useEffect(() => {
        setActiveTab("1"); // Reset to "All" tab when toggling online/offline
    }, [isOnline]);


    return (
        <div className="booking">
            {/* Toggle Online/Offline */}
            <div className="booking_header1">
                <div className="booking_header1_left"><h2>List of all classes</h2></div>
                <div className="online-toggle">
                    <div className="tab-navigation">
                        <button
                            className={`tab-button ${isOnline ? "active" : ""}`}
                            onClick={() => { if (!isOnline) setIsOnline(true); }}
                        >
                            Online
                        </button>
                        <button
                            className={`tab-button ${!isOnline ? "active" : ""}`}
                            onClick={() => { if (isOnline) setIsOnline(false); }}
                        >
                            Offline
                        </button>
                    </div>
                </div>
                <div className="booking_header1_right">
                    
                      <div className="booking_header_filters">
                    <RangePicker 
                        onChange={handleDateChange} 
                        value={dateRange}
                        allowClear={true}
                    />
                    {(dateRange[0] || dateRange[1]) && (
                        <Button 
                            onClick={handleClearFilters}
                            style={{ marginLeft: '8px' }}
                        >
                            Clear Filters
                        </Button>
                    )}
                </div>
                
                </div>
              
            </div>



            <div className="booking_content">
                <div className="booking_content_custom">
                    <div
                        className={activeTab === "1" ? "tab selected" : "tab"}
                        onClick={() => {
                            setActiveTab("1");
                            if (isOnline) fetchAllBookingsCurrent();
                            if (!isOnline) fetchAllBookingsOfflineCurrent();
                        }}
                    >
                        Current
                    </div>
                    <div
                        className={activeTab === "2" ? "tab selected" : "tab"}
                        onClick={() => {
                            setActiveTab("2");
                            if (isOnline) fetchAllBookingsPast();
                            if (!isOnline) fetchAllBookingsOfflinePast();
                        }}
                    >
                        Past
                    </div>
                </div>
            </div>

            <Table
                columns={isOnline ? columns : offlineColumns}
                dataSource={isOnline ? tableData : offlineTableData}
                loading={isLoading}
                pagination={{}}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default Bookings;
